import clsx from 'clsx';
import { AlertTriangle, CircleAlert, Info, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CRITICALITIES, PROJECT_STATUSES } from '@saas-manager/shared';
import { ArrowUpRight } from 'lucide-react';
import { Banner, Button, Empty, Input, Metric, PageHeader, Pill, Segmented, Select, Spinner, Stripes } from '@/components/ui/core';
import { DigestCard } from '@/features/digest/Digest';
import { EnvRail, HealthGlyph } from '@/components/ui/status';
import { fr } from '@/i18n/fr';
import { relative } from '@/lib/format';
import { useApi, useCan } from '@/lib/hooks';
import { useSession } from '@/lib/session';
import type { Dashboard, DashboardProject } from '@/lib/types';

const CRIT_RANK = { critical: 0, high: 1, medium: 2, low: 3 } as const;
const SEV_ICON = {
  critical: <CircleAlert className="size-3.5 text-bad" aria-label="Critique" />,
  warning: <AlertTriangle className="size-3.5 text-warn" aria-label="Avertissement" />,
  info: <Info className="size-3.5 text-muted" aria-label="Information" />,
};

type SortKey = 'attention' | 'criticality' | 'status' | 'owner' | 'name';

function Fact({ label, value, tone, title }: { label: string; value: string; tone?: 'bad' | 'warn' | 'faint'; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <dt className="text-caption opacity-60">{label}</dt>
      <dd className={clsx('truncate text-[0.8125rem]', tone === 'bad' && 'text-bad', tone === 'warn' && 'text-warn', tone === 'faint' && 'text-faint')}>{value}</dd>
    </div>
  );
}

export function ProjectRow({ p }: { p: DashboardProject }) {
  const changelog = !p.changelog ? { v: 'Non vérifié', t: 'faint' as const } : !p.changelog.found ? { v: 'Absent', t: 'warn' as const } : !p.changelog.valid ? { v: 'Non conforme', t: 'warn' as const } : { v: `Conforme · ${p.changelog.latestVersion ?? '—'}`, t: undefined };
  const reasons = p.attention.filter((r) => r.severity !== 'info');
  const hot = reasons.some((r) => r.severity === 'critical');
  return (
    <li>
      <Link
        to={`/projects/${p.slug}`}
        className={clsx(
          'group relative flex h-full flex-col rounded-[26px] p-5 transition-transform duration-150 hover:-translate-y-0.5 focus-visible:-translate-y-0.5',
          hot ? 'bg-panel text-panel-ink shadow-[0_24px_50px_-28px_rgba(0,0,0,.7)] ring-1 ring-lime/35' : 'glass',
        )}
      >
        <span aria-hidden className={clsx('absolute top-4 right-4 flex size-9 items-center justify-center rounded-full ring-1 transition-colors', hot ? 'ring-white/15 group-hover:bg-lime group-hover:text-lime-ink' : 'ring-hairline group-hover:bg-lime group-hover:ring-lime')}>
          <ArrowUpRight className="size-4" />
        </span>
        <div className="flex items-center gap-2.5 pr-12">
          <HealthGlyph health={p.health} size={14} />
          <span className="truncate text-[1.0625rem] font-semibold tracking-tight">{p.name}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Pill tone={p.criticality === 'critical' ? 'bad' : p.criticality === 'high' ? 'warn' : 'neutral'}>{fr.criticality[p.criticality]}</Pill>
          <Pill tone={hot ? 'lime' : 'neutral'}>{fr.projectStatus[p.status as keyof typeof fr.projectStatus]}</Pill>
        </div>
        {reasons.length > 0 ? (
          <ul className="mt-3 space-y-1">
            {reasons.slice(0, 3).map((r) => (
              <li key={r.code + r.message} className="flex items-center gap-1.5 text-[0.8125rem]">
                {SEV_ICON[r.severity]}
                <span className="truncate">{r.message}</span>
              </li>
            ))}
            {reasons.length > 3 && <li className={clsx('pl-5 text-caption', hot ? 'text-panel-muted' : 'text-muted')}>et {reasons.length - 3} autre(s) point(s)</li>}
          </ul>
        ) : (
          <p className={clsx('mt-3 line-clamp-2 text-[0.8125rem]', hot ? 'text-panel-muted' : 'text-muted')}>{p.description || 'Aucun point à traiter.'}</p>
        )}
        <div className="mt-4">
          <EnvRail envs={p.environments} />
        </div>
        <dl className={clsx('mt-auto grid grid-cols-2 gap-x-4 gap-y-3 border-t pt-4 sm:grid-cols-3', hot ? 'border-white/10' : 'border-hairline', 'mt-5')}>
          <Fact label="Déployé" value={p.lastDeployment ? `${p.lastDeployment.version} · ${relative(p.lastDeployment.at)}` : 'Inconnu'} tone={p.lastDeployment ? undefined : 'faint'} />
          <Fact label="Sauvegarde" value={p.lastSuccessfulBackup ? relative(p.lastSuccessfulBackup.at) : 'Aucune'} tone={p.lastSuccessfulBackup ? undefined : 'warn'} />
          <Fact label="Prochaine" value={p.nextBackup ? relative(p.nextBackup.at) : 'Non planifiée'} tone={p.nextBackup ? undefined : 'faint'} title={p.nextBackup?.policy} />
          <Fact label="Restauration testée" value={p.lastRestoreTest ? relative(p.lastRestoreTest.at) : 'Jamais'} tone={p.lastRestoreTest ? undefined : p.criticality === 'critical' || p.criticality === 'high' ? 'warn' : 'faint'} />
          <Fact label="Changelog" value={changelog.v} tone={changelog.t} />
        </dl>
      </Link>
    </li>
  );
}

/** Summary card: large figures and hatched gauges (share of SaaS in good standing). */
export function Overview({ data }: { data: Dashboard }) {
  const ps = data.projects;
  const n = Math.max(ps.length, 1);
  const day = 26 * 3_600_000;
  const now = Date.now();
  const backed = ps.filter((p) => p.lastSuccessfulBackup && now - new Date(p.lastSuccessfulBackup.at).getTime() < day).length;
  const healthy = ps.filter((p) => p.health === 'operational').length;
  const compliant = ps.filter((p) => p.changelog?.valid).length;
  const tested = ps.filter((p) => p.lastRestoreTest).length;
  const t = data.totals;
  const gauges = [
    { label: 'Sauvegardés < 26 h', v: backed },
    { label: 'Opérationnels', v: healthy },
    { label: 'Restauration testée', v: tested },
    { label: 'Changelog conforme', v: compliant },
  ];
  return (
    <div className="mb-6 grid gap-4 lg:grid-cols-[1.7fr_1fr]">
      <section className="glass rounded-[28px] p-6" aria-label="Synthèse">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          <Metric label="SaaS suivis" value={t.projects} />
          <Metric label="À traiter" value={t.attention} tone={t.attention ? 'warn' : undefined} />
          <Metric label="Alertes ouvertes" value={t.openAlerts} />
          <Metric label="Critiques" value={t.critical} tone={t.critical ? 'bad' : 'ok'} />
        </div>
        <div className="mt-7 grid gap-x-6 gap-y-4 sm:grid-cols-2">
          {gauges.map((g) => (
            <div key={g.label}>
              <div className="mb-1.5 flex items-baseline justify-between text-caption">
                <span className="font-medium text-muted">{g.label}</span>
                <span className="num font-semibold">{g.v}/{ps.length}</span>
              </div>
              <Stripes value={g.v / n} label={g.label} />
            </div>
          ))}
        </div>
      </section>
      <section className="flex flex-col rounded-[28px] bg-panel p-6 text-panel-ink" aria-label="Santé en direct">
        <div className="flex items-center justify-between">
          <p className="text-caption font-medium text-panel-muted">Santé en direct</p>
          <span className="num text-caption text-panel-muted">{relative(data.generatedAt)}</span>
        </div>
        <p className="num mt-1 text-metric font-light">
          {healthy}
          <span className="text-body text-panel-muted"> / {ps.length} opérationnel(s)</span>
        </p>
        <ul className="mt-4 flex flex-1 flex-wrap content-start gap-2">
          {ps.map((p) => (
            <li key={p.id}>
              <Link to={`/projects/${p.slug}`} className={clsx('flex h-9 items-center gap-2 rounded-full px-3.5 text-[0.8125rem] font-semibold transition-colors', p.alerts.critical ? 'bg-lime text-lime-ink' : 'bg-panel-2 hover:bg-white/15')}>
                <HealthGlyph health={p.health} size={11} />
                {p.name}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function DashboardPage() {
  const { data, isLoading, error, dataUpdatedAt } = useApi<Dashboard>('/dashboard', { refetchInterval: 30_000 });
  const directory = useApi<{ id: string; name: string }[]>('/directory');
  const allowed = useCan();
  const { me } = useSession();
  const navigate = useNavigate();
  const setup = useApi<{ remaining: string[]; complete: boolean; dismissedAt: string | null }>(allowed('settings.manage') ? '/setup' : null);
  const [view, setView] = useState<'attention' | 'all'>('attention');
  const [sort, setSort] = useState<SortKey>('attention');
  const [status, setStatus] = useState('');
  const [criticality, setCriticality] = useState('');
  const [query, setQuery] = useState('');

  const ownerName = (id: string | null | undefined) => directory.data?.find((u) => u.id === id)?.name ?? '';

  const list = useMemo(() => {
    let rows = data?.projects ?? [];
    if (view === 'attention') rows = rows.filter((p) => p.needsAttention);
    if (status) rows = rows.filter((p) => p.status === status);
    if (criticality) rows = rows.filter((p) => p.criticality === criticality);
    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter((p) => [p.name, p.slug, p.description, ...p.tags].some((s) => s.toLowerCase().includes(q)));
    }
    const score = (p: DashboardProject) => p.attention.reduce((n, r) => n + (r.severity === 'critical' ? 100 : r.severity === 'warning' ? 10 : 0), 0);
    const by: Record<SortKey, (a: DashboardProject, b: DashboardProject) => number> = {
      attention: (a, b) => score(b) - score(a) || CRIT_RANK[a.criticality] - CRIT_RANK[b.criticality],
      criticality: (a, b) => CRIT_RANK[a.criticality] - CRIT_RANK[b.criticality],
      status: (a, b) => PROJECT_STATUSES.indexOf(a.status as never) - PROJECT_STATUSES.indexOf(b.status as never),
      owner: (a, b) => ownerName(a.ownerUserId).localeCompare(ownerName(b.ownerUserId)),
      name: (a, b) => a.name.localeCompare(b.name),
    };
    return [...rows].sort(by[sort]);
  }, [data, view, sort, status, criticality, query, directory.data]);

  if (isLoading) return <Spinner />;
  if (error) return <Banner tone="critical" title="Tableau de bord indisponible">{error.message}</Banner>;
  const t = data!.totals;

  const subtitle =
    t.projects === 0
      ? 'Ajoutez votre premier SaaS pour suivre sa santé, ses sauvegardes et son changelog.'
      : t.attention === 0
        ? `${t.projects} SaaS suivis. Aucun ne demande d’intervention pour l’instant.`
        : `${t.attention} SaaS sur ${t.projects} demandent une intervention${t.critical ? `, dont ${t.critical} alerte(s) critique(s)` : ''}.`;

  return (
    <>
      <PageHeader
        title={`Bonjour${me?.user.name ? `, ${me.user.name.split(' ')[0]}` : ''}`}
        subtitle={subtitle}
        actions={
          allowed('project.create') && (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => navigate('/projects/new')}>
              Ajouter un SaaS
            </Button>
          )
        }
      />

      {setup.data && !setup.data.complete && !setup.data.dismissedAt && (
        <Banner tone="info" title={`Configuration initiale : ${setup.data.remaining.length} étape(s) restante(s)`} action={<Button variant="primary" size="sm" onClick={() => navigate('/setup')}>Continuer</Button>}>
          Connectez votre serveur, Cloudflare, le stockage des sauvegardes et vos canaux de notification, étape par étape.
        </Banner>
      )}
      {t.projects > 0 && <Overview data={data!} />}
      {t.projects > 0 && allowed('audit.read') && <DigestCard />}
      {t.projects === 0 ? (
        <div className="glass rounded-[28px]">
          <Empty title="Aucun SaaS suivi" action={allowed('project.create') && <Button variant="primary" onClick={() => navigate('/projects/new')}>Ajouter un SaaS</Button>}>
            L’assistant vous guide : dépôts, stack, environnements, changelog et politique de sauvegarde. Vous pourrez reprendre la configuration à tout moment.
          </Empty>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Segmented
              label="Affichage"
              value={view}
              onChange={setView}
              options={[
                { value: 'attention', label: 'À traiter', badge: <span className="text-caption text-muted">{t.attention}</span> },
                { value: 'all', label: 'Tous', badge: <span className="text-caption text-muted">{t.projects}</span> },
              ]}
            />
            <div className="relative ml-auto w-full sm:w-56">
              <Search className="pointer-events-none absolute top-3 left-3.5 size-4 text-faint" aria-hidden />
              <Input aria-label="Rechercher" placeholder="Rechercher" className="pl-10" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <Select aria-label="Statut" className="w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Tous les statuts</option>
              {PROJECT_STATUSES.filter((s) => s !== 'archived').map((s) => (
                <option key={s} value={s}>
                  {fr.projectStatus[s]}
                </option>
              ))}
            </Select>
            <Select aria-label="Criticité" className="w-auto" value={criticality} onChange={(e) => setCriticality(e.target.value)}>
              <option value="">Toutes criticités</option>
              {CRITICALITIES.map((c) => (
                <option key={c} value={c}>
                  {fr.criticality[c]}
                </option>
              ))}
            </Select>
            <Select aria-label="Trier par" className="w-auto" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="attention">Trier par urgence</option>
              <option value="criticality">Trier par criticité</option>
              <option value="status">Trier par statut</option>
              <option value="owner">Trier par responsable</option>
              <option value="name">Trier par nom</option>
            </Select>
          </div>

          {list.length === 0 ? (
            <div className="glass rounded-[28px]">
              <Empty title={view === 'attention' ? 'Rien à traiter' : 'Aucun résultat'}>
                {view === 'attention' ? 'Tous les SaaS ont des contrôles récents, des sauvegardes à jour et un changelog conforme.' : 'Modifiez les filtres pour afficher d’autres SaaS.'}
              </Empty>
            </div>
          ) : (
            <ul className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
              {list.map((p) => (
                <ProjectRow key={p.id} p={p} />
              ))}
            </ul>
          )}
          <p className="mt-2 px-1 text-caption text-faint">
            Données agrégées par la console, actualisées {relative(new Date(dataUpdatedAt))}. Une santé « inconnue » signifie qu’aucun contrôle récent n’existe.
          </p>
        </>
      )}
    </>
  );
}
